import { colorPrimitives } from 'components/Theme'
import { Stack } from '@mui/material'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import { useGetPatronStats } from 'hooks/api/useGetPatronStats'
import { useSetMe } from 'stores/useMeQuery'
import { type GridColDef } from '@mui/x-data-grid'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { DataTable } from 'components/Shared/DataTable'

interface PatronStatsTableProps {
  startDate: Date
  endDate: Date
  licensedEstablishmentId: number
}

const columns: GridColDef[] = [
  {
    field: 'licensedEstablishmentName',
    headerName: 'Licensed Establishment',
    minWidth: 130,
    sortingOrder: ['asc', 'desc'],
    flex: 2,
    valueFormatter: ({ value }) => value,
  },
  {
    field: 'averageAge',
    headerName: 'Average Age',
    minWidth: 120,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueFormatter: ({ value }) => value,
  },
  {
    field: 'femalePatronsPercentage',
    headerName: 'Female Patrons Percentage',
    minWidth: 120,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueFormatter: ({ value }) => value,
  },
  {
    field: 'malePatronsPercentage',
    headerName: 'Male Patrons Percentage',
    minWidth: 120,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueFormatter: ({ value }) => value,
  },
]

export const PatronStatsTable = ({
  startDate,
  endDate,
  licensedEstablishmentId,
}: PatronStatsTableProps) => {
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const patronCheckinQuery = useGetPatronStats({
    corporateAccountId: selectedCorporateAccountId,
    startDate,
    endDate,
    licensedEstablishmentId,
  })

  if (meQuery.isError || patronCheckinQuery.isError) {
    return <p>An error occurred.</p>
  }

  if (patronCheckinQuery.data?.length === 0) {
    return <NoDataIndicator />
  }

  return (
    <Stack spacing={2}>
      {patronCheckinQuery.data && (
        <DataTable
          slotProps={{
            row: {
              style: { cursor: 'default' },
            },
          }}
          noDataMessage="There are no patron stats related to this licensed establishment in the selected date range."
          columns={columns}
          rows={patronCheckinQuery.data ?? []}
          sx={{
            color: colorPrimitives.black,
            '& .MuiDataGrid-row:hover': {
              cursor: 'auto',
            },
          }}
        />
      )}
    </Stack>
  )
}
